// src/components/billing/ClientBillingPage.jsx
//
// Client "Billing" page (/billing): balance due, every invoice for the
// client's vessels with its payment status, the Xero PDF, and in-app card
// payment.
//
// Data: GET /api/v2/client/billing/invoices - invoices for the Supabase
// yachts linked to this client's sites (the same link the Starlink quota
// uses), so there is nothing new for admins to set up per client.
//
// Who sees it: client roles in BILLING_CLIENT_ROLES on the server (default
// account admins only). The nav link uses the same default; if you widen it
// on the server, widen CLIENT_NAV_LINKS in FleetHeader.jsx too.

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../../api/apiClient';
import FleetHeader from '../FleetHeader';
import InvoiceDrawer from './InvoiceDrawer';
import { StatusBadge, formatMoney, formatDate, LoadingScreen } from './billingShared';
import '../../styles/fleet-theme.css';
import './Billing.css';

const PaySheet = lazy(() => import('./PaySheet'));
const PdfPreview = lazy(() => import('./PdfPreview'));

const FILTERS = [
  { id: 'open', label: 'To pay', match: (i) => ['open', 'partial', 'overdue'].includes(i.status) },
  { id: 'paid', label: 'Paid', match: (i) => ['paid', 'processing'].includes(i.status) },
  { id: 'all', label: 'All', match: () => true },
];

export default function ClientBillingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [paying, setPaying] = useState(null);
  const [previewInv, setPreviewInv] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      setData(await apiFetchJSON('/api/v2/client/billing/invoices'));
    } catch (e) {
      setError(e.status === 403 ? 'Billing is only available to account administrators.' : (e.message || 'Could not load invoices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rare: Stripe had to leave the page for a 3-D Secure step and came back.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const pi = p.get('payment_intent');
    if (!pi) return;
    navigate('/billing', { replace: true });
    (async () => {
      try {
        const s = await apiFetchJSON(`/api/v2/client/billing/payments/${encodeURIComponent(pi)}/status`);
        setToast(s.stripe_status === 'succeeded'
          ? `Payment received for invoice ${s.invoice_number}.`
          : 'The payment was not completed. You can try again.');
      } catch { /* ignore */ }
      load({ silent: true });
    })();
  }, [location.search, navigate, load]);

  const invoices = data?.invoices || [];
  const toPay = invoices.filter(FILTERS[0].match);
  const activeFilter = filter || (toPay.length ? 'open' : 'all');
  const rows = useMemo(
    () => invoices.filter(FILTERS.find((f) => f.id === activeFilter).match),
    [invoices, activeFilter]
  );
  // Show which company issued each invoice only when there's more than one.
  const multiCompany = new Set(invoices.map((i) => i.business_unit)).size > 1;
  const owing = (data?.summary || []).filter((b) => b.outstanding > 0);
  const overdueCount = owing.reduce((n, b) => n + b.overdue_count, 0);

  const startPay = (inv, e) => {
    e && e.stopPropagation();
    setPaying(inv);
  };

  const closePay = (paid) => {
    setPaying(null);
    if (paid) {
      setOpenId(null);
      load({ silent: true });
    }
  };

  if (loading && !data) return <LoadingScreen text="Loading invoices…" />;

  return (
    <div>
      <FleetHeader title="Billing" user={user} onLogout={onLogout} scope="client" />
      <div className="sd-page bl-page">
        {error && <div className="ft-toolbar-error" role="alert">{error}</div>}
        {toast && (
          <div className="bl-toast" role="status">
            {toast}
            <button type="button" className="bl-icon-btn" onClick={() => setToast(null)} aria-label="Dismiss"><i className="fas fa-times"></i></button>
          </div>
        )}

        {data && (
          <>
            <section className={`bl-hero ${overdueCount ? 'has-overdue' : ''}`}>
              <div className="bl-hero-label">{owing.length ? 'Balance due' : 'Balance'}</div>
              {owing.length === 0 ? (
                <div className="bl-hero-amount ft-mono">All paid up</div>
              ) : (
                owing.map((b) => (
                  <div className="bl-hero-amount ft-mono" key={b.currency}>{formatMoney(b.outstanding, b.currency)}</div>
                ))
              )}
              <div className="bl-hero-note">
                {owing.length === 0
                  ? (invoices.length ? 'Nothing outstanding on your account.' : 'No invoices yet.')
                  : `${toPay.length} ${toPay.length === 1 ? 'invoice' : 'invoices'} to pay` +
                    (overdueCount ? `, ${overdueCount} overdue` : '')}
              </div>
              {!data.linked && (
                <p className="bl-note">Your vessels aren't linked to billing yet. Contact us and we'll set it up.</p>
              )}
              {toPay.length > 0 && !toPay.some((i) => i.can_pay_online) && (
                <p className="bl-note">Online card payment isn't available for these invoices yet. Use the payment details on the invoice PDF.</p>
              )}
            </section>

            <div className="ft-filter-row bl-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={'ft-pill' + (activeFilter === f.id ? ' active' : '')}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span className="bl-pill-count">{invoices.filter(f.match).length}</span>
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className="bl-empty">
                {activeFilter === 'open' ? 'No invoices waiting for payment.' : 'No invoices here.'}
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="bl-table-wrap">
                  <table className="ft-table bl-table">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Vessel</th>
                        <th>Issued</th>
                        <th>Due</th>
                        <th className="ft-num">Total</th>
                        <th className="ft-num">Balance</th>
                        <th>Status</th>
                        <th aria-label="Actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((inv) => (
                        <tr key={inv.id} className="bl-row" onClick={() => setOpenId(inv.id)} tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setOpenId(inv.id)}>
                          <td className="ft-mono">
                            {inv.invoice_number}
                            {multiCompany && <div className="bl-small bl-muted">{inv.company}</div>}
                          </td>
                          <td>{inv.site_name || inv.yacht_name || '—'}</td>
                          <td>{formatDate(inv.issue_date)}</td>
                          <td className={inv.status === 'overdue' ? 'bl-overdue-text' : ''}>{formatDate(inv.due_date)}</td>
                          <td className="ft-num">{formatMoney(inv.total, inv.currency)}</td>
                          <td className="ft-num">{inv.status === 'paid' ? '—' : formatMoney(inv.amount_due, inv.currency)}</td>
                          <td><StatusBadge status={inv.status} daysOverdue={inv.days_overdue} /></td>
                          <td className="bl-actions-cell">
                            <button type="button" className="bl-btn-ghost" onClick={(e) => { e.stopPropagation(); setPreviewInv(inv); }} aria-label={`View PDF of ${inv.invoice_number}`} title="View PDF">
                              <i className="fas fa-file-pdf"></i>
                            </button>
                            {inv.can_pay_online && (
                              <button type="button" className="bl-btn-primary bl-btn-sm" onClick={(e) => startPay(inv, e)}>Pay</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Phone / app cards */}
                <div className="bl-cards">
                  {rows.map((inv) => (
                    <div key={inv.id} className={`bl-card ${inv.status === 'overdue' ? 'is-overdue' : ''}`} onClick={() => setOpenId(inv.id)}>
                      <div className="bl-card-top">
                        <span className="ft-mono">{inv.invoice_number}</span>
                        <StatusBadge status={inv.status} daysOverdue={inv.days_overdue} />
                      </div>
                      <div className="bl-card-amount ft-mono">
                        {formatMoney(inv.status === 'paid' ? inv.total : inv.amount_due, inv.currency)}
                      </div>
                      <div className="bl-card-meta">
                        {multiCompany ? `${inv.company} · ` : ''}{inv.site_name || inv.yacht_name || '—'} · {inv.status === 'paid' ? `Issued ${formatDate(inv.issue_date)}` : `Due ${formatDate(inv.due_date)}`}
                      </div>
                      {inv.can_pay_online && (
                        <button type="button" className="bl-btn-primary bl-btn-block" onClick={(e) => startPay(inv, e)}>
                          Pay {formatMoney(inv.amount_due, inv.currency)}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {openId && (
        <InvoiceDrawer
          scope="client"
          invoiceId={openId}
          onClose={() => setOpenId(null)}
          onPay={(inv) => startPay(inv)}
        />
      )}

      {previewInv && (
        <Suspense fallback={null}>
          <PdfPreview
            url={`/api/v2/client/billing/invoices/${encodeURIComponent(previewInv.id)}/pdf`}
            invoiceNumber={previewInv.invoice_number}
            onClose={() => setPreviewInv(null)}
          />
        </Suspense>
      )}

      {paying && (
        <Suspense fallback={null}>
          <PaySheet
            invoice={paying}
            onClose={closePay}
            onPaid={() => load({ silent: true })}
          />
        </Suspense>
      )}
    </div>
  );
}